"use client";

import { useEffect, useRef, useState } from "react";

import {
  Check,
  FileText,
  GripVertical,
  MousePointer2,
  Play,
  Share2,
} from "lucide-react";

import { cn } from "@/lib/utils";

// folktale.ai's animated "Mixed-method collection" card headers, rebuilt with
// CSS keyframes (globals.css `site-cap-*`) instead of framer-motion.

export type SurveyLabels = {
  question: string;
  options: [string, string, string];
};

const SCENE_MS = 2800;
const EXIT_MS = 450;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Cycles through `count` scenes, flagging the last 450ms of each as leaving. */
function useSceneCycle(count: number) {
  const [scene, setScene] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let swap: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      setLeaving(true);
      swap = setTimeout(() => {
        setScene((current) => (current + 1) % count);
        setLeaving(false);
      }, EXIT_MS);
    }, SCENE_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(swap);
    };
  }, [count]);

  return { scene, leaving };
}

/** Enter/exit wrapper; `from`/`to` are the transforms it animates in from and out to. */
function Scene({
  leaving,
  from,
  to = from,
  className,
  children,
}: {
  leaving: boolean;
  from: string;
  to?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(leaving ? "site-cap-out" : "site-cap-in", className)}
      style={{ "--cap-from": from, "--cap-to": to } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- Collect

function VideoScene() {
  return (
    <div className="relative flex items-center justify-center">
      {[0, 0.6, 1.2].map((delay) => (
        <span
          key={delay}
          className="site-cap-ripple bg-site-forest/20 absolute h-12 w-12 rounded-full"
          style={{ animationDelay: `${delay}s` }}
        />
      ))}
      <div className="site-cap-pulse bg-site-forest relative flex h-12 w-12 items-center justify-center rounded-full shadow-md">
        <Play
          className="fill-site-cream text-site-cream h-5 w-5"
          strokeWidth={0}
        />
      </div>
    </div>
  );
}

const WAVE_BARS = Array.from({ length: 28 }, (_, i) => {
  const r = (Math.sin(i * 1.3) + 1) / 2;
  return { a: 20 + r * 30, b: 60 + r * 40, c: 25 + r * 35, delay: i * 0.04 };
});

function AudioScene() {
  return (
    <div className="flex h-16 items-center justify-center gap-[3px]">
      {WAVE_BARS.map((bar, i) => (
        <span
          key={i}
          className="site-cap-wave bg-site-forest w-[3px] rounded-full"
          style={
            {
              "--wave-a": `${bar.a}%`,
              "--wave-b": `${bar.b}%`,
              "--wave-c": `${bar.c}%`,
              animationDelay: `${bar.delay}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

function SurveyScene({ labels }: { labels: SurveyLabels }) {
  return (
    <div className="w-full max-w-[220px] rounded-lg bg-white/95 p-2.5 shadow-sm backdrop-blur">
      <div className="text-site-forest mb-1.5 text-[10px] font-medium">
        {labels.question}
      </div>
      <div className="space-y-1">
        {labels.options.map((option, i) => {
          const picked = i === 0;
          return (
            <div
              key={option}
              className={cn(
                "site-cap-slide flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px]",
                picked
                  ? "bg-site-forest text-site-cream"
                  : "bg-site-forest/5 text-site-forest/70",
              )}
              style={{ animationDelay: `${0.2 + i * 0.15}s` }}
            >
              <span
                className={cn(
                  "flex h-3 w-3 items-center justify-center rounded-full border",
                  picked
                    ? "border-site-cream bg-site-cream/20"
                    : "border-site-forest/30",
                )}
              >
                {picked ? (
                  <span
                    className="site-cap-pop"
                    style={{ animationDelay: "0.75s" }}
                  >
                    <Check
                      className="text-site-cream h-2 w-2"
                      strokeWidth={4}
                    />
                  </span>
                ) : null}
              </span>
              {option}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CollectVisual({ survey }: { survey: SurveyLabels }) {
  const { scene, leaving } = useSceneCycle(3);
  return (
    <div className="absolute inset-0 flex items-center justify-center p-4">
      {scene === 0 ? (
        <Scene key="video" leaving={leaving} from="scale(0.6)">
          <VideoScene />
        </Scene>
      ) : null}
      {scene === 1 ? (
        <Scene key="audio" leaving={leaving} from="scaleY(0.2)">
          <AudioScene />
        </Scene>
      ) : null}
      {scene === 2 ? (
        <Scene
          key="survey"
          leaving={leaving}
          from="translateY(10px)"
          to="translateY(-10px)"
          className="flex w-full justify-center"
        >
          <SurveyScene labels={survey} />
        </Scene>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Analyse

const PLEXUS_NODES = [
  { x: 30, y: 40, big: false },
  { x: 75, y: 25, big: true },
  { x: 120, y: 55, big: false },
  { x: 160, y: 30, big: false },
  { x: 55, y: 90, big: false },
  { x: 110, y: 110, big: true },
  { x: 170, y: 95, big: false },
  { x: 35, y: 130, big: false },
  { x: 145, y: 140, big: true },
];
const PLEXUS_ORDER = [4, 1, 7, 0, 5, 2, 8, 3, 6];
const nodeDelay = (i: number) => PLEXUS_ORDER.indexOf(i) * 0.12;
const PLEXUS_EDGES: [number, number][] = [
  [0, 1],
  [1, 2],
  [2, 3],
  [0, 4],
  [1, 4],
  [2, 5],
  [3, 6],
  [4, 5],
  [5, 6],
  [4, 7],
  [5, 8],
  [6, 8],
  [7, 5],
];
// Faint spokes from every hub node to every other node.
const PLEXUS_SPOKES = PLEXUS_NODES.flatMap((node, hub) =>
  node.big
    ? PLEXUS_NODES.map((_, other) => [hub, other] as [number, number]).filter(
        ([a, b]) => a !== b,
      )
    : [],
);

function PlexusScene() {
  const line = (
    [a, b]: [number, number],
    key: string,
    width: string,
    opacity: number,
    lag: number,
  ) => (
    <line
      key={key}
      x1={PLEXUS_NODES[a].x}
      y1={PLEXUS_NODES[a].y}
      x2={PLEXUS_NODES[b].x}
      y2={PLEXUS_NODES[b].y}
      stroke="var(--color-site-forest)"
      strokeWidth={width}
      pathLength={1}
      className="site-cap-draw"
      style={
        {
          "--draw-opacity": opacity,
          animationDelay: `${Math.max(nodeDelay(a), nodeDelay(b)) + lag}s`,
        } as React.CSSProperties
      }
    />
  );
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full">
      {PLEXUS_SPOKES.map((edge, i) => line(edge, `s-${i}`, "0.5", 0.22, 0.2))}
      {PLEXUS_EDGES.map((edge, i) => line(edge, `e-${i}`, "1", 0.5, 0.15))}
      {PLEXUS_NODES.map((node, i) => (
        <circle
          key={i}
          cx={node.x}
          cy={node.y}
          r={node.big ? 5 : 2.6}
          fill="var(--color-site-forest)"
          className={node.big ? "site-cap-node-big" : "site-cap-node"}
          style={{ animationDelay: `${nodeDelay(i)}s` }}
        />
      ))}
    </svg>
  );
}

const PIE = [
  { value: 65, colour: "var(--color-site-forest)" },
  { value: 35, colour: "oklch(0.72 0.15 55)" },
];

function pieSlice(fromPct: number, toPct: number) {
  const r = 48;
  const a0 = (fromPct / 100) * Math.PI * 2 - Math.PI / 2;
  const a1 = (toPct / 100) * Math.PI * 2 - Math.PI / 2;
  const large = toPct - fromPct > 50 ? 1 : 0;
  return `M 70 70 L ${70 + Math.cos(a0) * r} ${70 + Math.sin(a0) * r} A ${r} ${r} 0 ${large} 1 ${70 + Math.cos(a1) * r} ${70 + Math.sin(a1) * r} Z`;
}

const PIE_PATHS = PIE.map((slice, i) => {
  const from = PIE.slice(0, i).reduce((sum, s) => sum + s.value, 0);
  return { d: pieSlice(from, from + slice.value), colour: slice.colour };
});

function PieScene() {
  return (
    <svg viewBox="0 0 140 140" className="h-full">
      {PIE_PATHS.map((slice, i) => (
        <path
          key={i}
          d={slice.d}
          fill={slice.colour}
          className="site-cap-slice"
          style={{ animationDelay: `${0.15 + i * 0.25}s` }}
        />
      ))}
    </svg>
  );
}

const CONTENT_LINES = [
  { w: "70%", strong: true },
  { w: "92%", strong: false },
  { w: "85%", strong: false },
  { w: "60%", strong: false },
  { w: "78%", strong: false },
];

function ContentScene() {
  return (
    <div className="w-full max-w-[240px] space-y-1.5 rounded-lg bg-white/95 p-3 shadow-sm backdrop-blur">
      {CONTENT_LINES.map((line, i) => (
        <div
          key={i}
          className={cn(
            "site-cap-grow rounded-full",
            line.strong
              ? "bg-site-forest"
              : i % 2
                ? "bg-site-forest/30"
                : "bg-site-forest/50",
          )}
          style={{
            width: line.w,
            height: line.strong ? 10 : 6,
            animationDelay: `${0.15 + i * 0.12}s`,
          }}
        />
      ))}
      <div
        className="site-cap-fade flex gap-1 pt-1"
        style={{ animationDelay: "0.9s" }}
      >
        <span className="bg-site-forest/15 h-4 w-10 rounded" />
        <span className="bg-site-forest/15 h-4 w-8 rounded" />
        <span className="bg-site-forest/15 h-4 w-12 rounded" />
      </div>
    </div>
  );
}

function AnalyseVisual() {
  const { scene, leaving } = useSceneCycle(3);
  return (
    <div className="absolute inset-0 flex items-center justify-center p-4">
      {scene === 0 ? (
        <Scene
          key="plexus"
          leaving={leaving}
          from="scale(0.92)"
          className="h-full w-full"
        >
          <PlexusScene />
        </Scene>
      ) : null}
      {scene === 1 ? (
        <Scene
          key="pie"
          leaving={leaving}
          from="scale(0.7) rotate(-30deg)"
          to="scale(0.7) rotate(30deg)"
          className="h-full"
        >
          <PieScene />
        </Scene>
      ) : null}
      {scene === 2 ? (
        <Scene
          key="content"
          leaving={leaving}
          from="translateY(10px)"
          to="translateY(-10px)"
          className="flex w-full justify-center"
        >
          <ContentScene />
        </Scene>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Share

type Block = "text" | "chart" | "video" | "quote";

const INITIAL_ORDER: Block[] = ["text", "chart", "video", "quote"];
// Each step drags one block to a new slot; the loop ends back at INITIAL_ORDER.
const MOVES: [Block, number][] = [
  ["video", 0],
  ["quote", 1],
  ["chart", 3],
  ["text", 2],
];
const TILE_H = 36;
const TILE_GAP = 6;

const slotPosition = (slot: number) => ({
  left: slot % 2 === 0 ? 0 : `calc(50% + ${TILE_GAP / 2}px)`,
  top: Math.floor(slot / 2) * (TILE_H + TILE_GAP),
});

function TextBlock() {
  return (
    <div className="border-site-forest/20 h-9 space-y-1 rounded border border-dashed p-1 pl-2.5">
      <div className="bg-site-forest/40 h-1 w-[95%] rounded" />
      <div className="bg-site-forest/40 h-1 w-[82%] rounded" />
      <div className="bg-site-forest/30 h-1 w-[68%] rounded" />
    </div>
  );
}

function ChartBlock() {
  return (
    <div className="border-site-forest/15 bg-site-forest/5 flex h-9 items-end justify-around gap-[2px] rounded border p-1 pl-2.5">
      {[60, 30, 80, 45, 70, 35].map((h, i) => (
        <span
          key={i}
          className="bg-site-forest w-1 rounded-sm"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

function VideoBlock() {
  return (
    <div className="border-site-forest/15 bg-site-forest/10 relative flex h-9 items-center justify-center rounded border pl-2.5">
      <div className="bg-site-forest flex h-4 w-4 items-center justify-center rounded-full">
        <Play
          className="fill-site-cream text-site-cream h-2 w-2"
          strokeWidth={0}
        />
      </div>
      <span className="text-site-forest/60 absolute right-1 bottom-0.5 text-[7px] font-medium">
        0:42
      </span>
    </div>
  );
}

function QuoteBlock() {
  return (
    <div className="border-site-forest/60 bg-site-forest/5 flex h-9 flex-col justify-center gap-1 rounded border-l-2 p-1 pl-2.5">
      <div className="bg-site-forest/40 h-1 w-[88%] rounded" />
      <div className="bg-site-forest/30 h-1 w-[64%] rounded" />
    </div>
  );
}

const BLOCKS: Record<Block, () => React.ReactNode> = {
  text: TextBlock,
  chart: ChartBlock,
  video: VideoBlock,
  quote: QuoteBlock,
};

function ShareVisual() {
  const [order, setOrder] = useState<Block[]>(INITIAL_ORDER);
  const [active, setActive] = useState<Block | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let cancelled = false;
    const wait = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    (async () => {
      while (!cancelled) {
        await wait(900);
        for (const [block, slot] of MOVES) {
          if (cancelled) return;
          setActive(block);
          await wait(550);
          if (cancelled) return;
          setOrder((current) => {
            const next = current.filter((b) => b !== block);
            next.splice(slot, 0, block);
            return next;
          });
          await wait(450);
          if (cancelled) return;
          setActive(null);
          await wait(650);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeSlot = active ? order.indexOf(active) : -1;
  const cursor =
    activeSlot >= 0
      ? {
          left: activeSlot % 2 === 0 ? "28%" : "72%",
          top: Math.floor(activeSlot / 2) * (TILE_H + TILE_GAP) + TILE_H / 2,
        }
      : { left: "22%", top: 0 };

  return (
    <div className="absolute inset-0 p-3">
      <div className="relative h-full w-full rounded-lg bg-white/95 p-2.5 shadow-sm backdrop-blur">
        <div className="border-site-forest/10 mb-2 flex items-center justify-between border-b pb-1.5">
          <div className="flex items-center gap-1">
            <span className="bg-site-forest/30 h-1.5 w-1.5 rounded-full" />
            <span className="bg-site-forest/30 h-1.5 w-1.5 rounded-full" />
            <span className="bg-site-forest/30 h-1.5 w-1.5 rounded-full" />
          </div>
          <Share2
            className="text-site-forest/60 h-2.5 w-2.5"
            strokeWidth={2.5}
          />
        </div>
        <div className="mb-1.5 flex items-center gap-1.5">
          <FileText className="text-site-forest h-2.5 w-2.5" strokeWidth={2} />
          <div className="bg-site-forest h-2 w-[70%] rounded" />
        </div>
        <div className="relative" style={{ height: TILE_H * 2 + TILE_GAP }}>
          {INITIAL_ORDER.map((block) => {
            const Content = BLOCKS[block];
            const isActive = active === block;
            return (
              <div
                key={block}
                className={cn(
                  "absolute transition-all duration-300 ease-out",
                  isActive
                    ? "z-[5] scale-105 shadow-[0_8px_18px_-6px_rgba(0,0,0,0.2)]"
                    : "z-[1]",
                  active && !isActive ? "opacity-55" : "opacity-100",
                )}
                style={{
                  ...slotPosition(order.indexOf(block)),
                  width: `calc(50% - ${TILE_GAP / 2}px)`,
                }}
              >
                <div
                  className={cn(
                    "absolute top-1/2 -left-0.5 -translate-y-1/2 transition-opacity",
                    isActive ? "opacity-100" : "opacity-40",
                  )}
                >
                  <GripVertical
                    className="text-site-forest/60 h-2.5 w-2.5"
                    strokeWidth={2}
                  />
                </div>
                <Content />
              </div>
            );
          })}
          <div
            className={cn(
              "pointer-events-none absolute transition-all duration-300 ease-out",
              active ? "scale-100 opacity-100" : "scale-90 opacity-55",
            )}
            style={cursor}
          >
            <MousePointer2
              className="text-site-forest h-3 w-3"
              strokeWidth={2.5}
              fill={active ? "currentColor" : "none"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Exports

export default function CollectionVisual({
  variant,
  survey,
  className,
}: {
  variant: "collect" | "analyse" | "share";
  survey: SurveyLabels;
  className?: string;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "from-site-forest/5 via-site-cream to-site-forest/10 relative h-44 w-full overflow-hidden rounded-xl bg-gradient-to-br",
        className,
      )}
    >
      {variant === "collect" ? <CollectVisual survey={survey} /> : null}
      {variant === "analyse" ? <AnalyseVisual /> : null}
      {variant === "share" ? <ShareVisual /> : null}
    </div>
  );
}

const SERVICE_LINES = Array.from({ length: 9 }, (_, i) => i);

/** The services banner's flowing lines: drawn in once on view, then a dot travels along. */
export function ServiceLines() {
  const ref = useRef<SVGSVGElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-60px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <svg
      ref={ref}
      aria-hidden="true"
      viewBox="0 0 400 300"
      preserveAspectRatio="xMidYMid slice"
      className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-[45%] md:block"
    >
      {SERVICE_LINES.map((i) => (
        <path
          key={i}
          d={`M -20 ${40 + i * 28} C 100 ${-10 + i * 30}, 220 ${110 + i * 22}, 420 ${20 + i * 32}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.2}
          strokeLinecap="round"
          pathLength={1}
          className={cn(
            i % 3 === 0 ? "text-site-terracotta/70" : "text-site-bone/25",
            seen ? "site-cap-line" : "opacity-0",
          )}
          style={{ animationDelay: `${i * 0.12}s` }}
        />
      ))}
      <circle r={4} className="site-cap-traveller fill-site-terracotta" />
    </svg>
  );
}
